import { useEffect, useState } from "react";
import { cn } from "@/design-system/lib/utils";
import { useAuth } from "@/hooks/useAuth";

/**
 * UX Enhancements §2. Mine / All toggle for list-page toolbars. By default
 * the toggle hides for C0–C4 roles (on pages whose lists are already
 * permission-scoped to the user's own items); pages with UNSCOPED lists
 * (e.g. Brokers) pass `forceVisible` so those roles get the control too.
 * Defaults to Mine for B-series (and C0/C1 when forced visible), All for
 * E-series / ADMIN. The state persists per user per page in localStorage.
 */
export interface MyToggleProps {
  pageKey: string;
  label?: string;
  onChange: (mine: boolean) => void;
  className?: string;
  /** Renders the toggle even for the C0–C4 roles that are hidden by
   * default. The default hiding assumes the page's list is already
   * permission-scoped to the user's own items — that is NOT true for the
   * Brokers page (Brokers.Read is unscoped), which was the "My brokers
   * doesn't filter" bug: the exact roles that needed the control never got
   * it. Pages whose lists are unscoped pass forceVisible; C0/C1 then
   * default to Mine. */
  forceVisible?: boolean;
}

const HIDDEN_ROLES = new Set(["C0", "C1", "C2", "C3", "C4"]);
const MINE_DEFAULT_PREFIXES = ["B"];
const FORCED_MINE_DEFAULT_ROLES = new Set(["C0", "C1"]);

function keyFor(userId: string | undefined, pageKey: string) {
  return `ux:mytoggle:${userId ?? "anon"}:${pageKey}`;
}

function defaultMine(primaryRole: string | undefined, forceVisible: boolean): boolean {
  if (!primaryRole) return false;
  if (primaryRole === "ADMIN") return false;
  if (MINE_DEFAULT_PREFIXES.some((p) => primaryRole.startsWith(p))) return true;
  if (forceVisible && FORCED_MINE_DEFAULT_ROLES.has(primaryRole)) return true;
  return false;
}

export function MyToggle({ pageKey, label = "Mine", onChange, className, forceVisible = false }: MyToggleProps) {
  const { user, primaryRoleCode } = useAuth();
  const primaryRole = primaryRoleCode ?? undefined;
  const hidden = !forceVisible && (primaryRole ? HIDDEN_ROLES.has(primaryRole) : false);

  const storageKey = keyFor(user?.id, pageKey);
  const initial = (() => {
    if (typeof window === "undefined") return defaultMine(primaryRole, forceVisible);
    const stored = window.localStorage.getItem(storageKey);
    if (stored === "mine") return true;
    if (stored === "all") return false;
    return defaultMine(primaryRole, forceVisible);
  })();
  const [mine, setMine] = useState<boolean>(initial);

  useEffect(() => {
    onChange(mine);
    // Intentionally skip onChange in the dep list — a stable callback from
    // the caller is assumed, and we only want to publish on mine changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine]);

  const set = (next: boolean) => {
    setMine(next);
    try {
      window.localStorage.setItem(storageKey, next ? "mine" : "all");
    } catch {
      /* localStorage unavailable — ignore */
    }
  };

  if (hidden) return null;

  return (
    <div
      role="group"
      aria-label="Scope"
      className={cn(
        "inline-flex rounded-md border border-border-light bg-white-pure overflow-hidden",
        className
      )}
    >
      <button
        type="button"
        onClick={() => set(true)}
        className={cn(
          "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
          mine ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
        )}
        aria-pressed={mine}
      >
        {label}
      </button>
      <button
        type="button"
        onClick={() => set(false)}
        className={cn(
          "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard",
          !mine ? "bg-gold text-onyx hover:bg-gold-highlight" : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10"
        )}
        aria-pressed={!mine}
      >
        All
      </button>
    </div>
  );
}
